import { useEffect, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BottomNav } from './BottomNav';
import { useAuthStore } from '@/stores/auth.store';
import { profileService } from '@/services/profile.service';

/**
 * Keeps the cached user (and its admin-set restrictions) fresh while the app
 * is open. Refetches /users/me on every tab mount, on window focus, and every
 * 30s — so lifting or adding a restriction in the admin panel reflects almost
 * immediately, without waiting for a cold reload / token expiry.
 */
function useProfileSync() {
  const token = useAuthStore((s) => s.accessToken);
  const updateUser = useAuthStore((s) => s.updateUser);
  const { data } = useQuery({
    queryKey: ['users', 'me', 'sync'],
    queryFn: profileService.getMe,
    enabled: !!token,
    staleTime: 15_000,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
  useEffect(() => {
    if (data) updateUser(data);
  }, [data, updateUser]);
}

export function Layout({ children, hideNav }: { children: ReactNode; hideNav?: boolean }) {
  useProfileSync();
  return (
    <div className="min-h-screen flex flex-col bg-bg-primary">
      <main
        className="flex-1 px-4"
        style={{
          // Top: device safe-area + a small comfort gap so the first card never
          // kisses the status bar (especially when Telegram is in fullscreen).
          paddingTop: 'calc(var(--safe-top) + 12px)',
          // Bottom: reserve space for the floating nav (≈64 px) + safe-area
          // when nav is visible; just safe-area otherwise.
          paddingBottom: hideNav
            ? 'calc(var(--safe-bottom) + 12px)'
            : // Nav box: 8 (top) + ≈52 (icon+label cell w/ inner pad) + 10 (bottom) + safe-bottom
               'calc(80px + var(--safe-bottom))',
        }}
      >
        {children}
      </main>
      {!hideNav && <BottomNav />}
    </div>
  );
}
